import { ACCOUNT_BINDING_REVIEW_FUNCTION, ADMIN_FUNCTIONS, REFUND_FUNCTIONS, REVIEWED_UNCLAIM_FUNCTION } from '../../config/functionRoutes';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { collection, onSnapshot, doc, updateDoc, serverTimestamp, arrayUnion } from 'firebase/firestore';
import { auth, db } from '../../config/firebase';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Typography, Box, TextField, Checkbox, FormControlLabel, Button, Dialog, DialogTitle, DialogContent, DialogActions, Alert, Chip, Divider, CircularProgress } from '@mui/material';
import RegistrationDetailsDialog from './RegistrationDetailsDialog';
import { getRegistrationById } from '../../services/registrationService';
import { listCodeList } from '../../services/codeListService';
import { Registration } from '../../types';
import { sendEmail, EmailType } from '../../services/emailService';
import AccountBindingReviewDialog from './AccountBindingReviewDialog';
import ReviewedUnclaimPrepareDialog from './ReviewedUnclaimPrepareDialog';
import AccountBindingPrepareDialog from './AccountBindingPrepareDialog';
import {
  AccountBindingReviewController,
  AccountBindingSelection,
  createAccountBindingReviewController,
} from '../../services/accountBindingReviewService';
import { createReviewedUnclaimReviewController } from '../../services/reviewedUnclaimReviewService';

// Local definition to replace the deleted statusService
interface RegistrationStatus {
  id: string;
  label: string;
}

interface AdminTask {
  registrationId: string;
  id: string;
  type: string;
  status: string;
  operationKey?: string;
  proposedBy?: string;
  description?: any;
  link?: string;
  createdAt?: Date;
  // Refund-specific fields
  refundAmount?: number;
  adminComment?: string;
  resolvedAt?: Date;
  resolvedBy?: string;
  refundResult?: any;
  requiresRefundReview?: boolean;
  cancellationReviewedAt?: Date;
  cancellationDecision?: string;
  refundReviewedAt?: Date;
  refundDecision?: string;
  // Auto-link task fields
  authUid?: string;
  authEmail?: string;
  authDisplayName?: string;
  newUserDocId?: string;
  existingDocId?: string;
  existingDisplayName?: string;
  existingFirstName?: string;
  existingLastName?: string;
  existingEmail?: string;
  existingPersonId?: number;
  existingYearOfBirth?: number;
  existingNationality?: string;
  matchType?: string;
  // Claim/unclaim task fields
  requesterUid?: string;
  requesterEmail?: string;
  requesterName?: string;
  targetProfileDocId?: string;
  targetPersonId?: number;
  targetName?: string;
  targetEmail?: string;
  targetYearOfBirth?: number;
  targetNationality?: string;
  profileDocId?: string;
  personId?: number;
  profileName?: string;
  profileEmail?: string;
  reason?: string;
  reviewProtocol?: string;
  // Unclaim-specific fields
  legacySourceUserId?: string;
  keepPersonalDetails?: boolean;
}

const isPlainRecord = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === 'object' && !Array.isArray(value);

const isStrictIdentityPolicy = () => process.env.REACT_APP_PERSON_IDENTITY_POLICY === 'strict-v1';

const accountBindingSelectionFromInput = (input: unknown): AccountBindingSelection | null => {
  if (!isPlainRecord(input) || typeof input.authUid !== 'string' || !input.authUid
    || !(input.formerUserId === null || (typeof input.formerUserId === 'string' && !!input.formerUserId))
    || typeof input.targetUserId !== 'string' || !input.targetUserId
    || typeof input.targetPersonId !== 'number' || !Number.isSafeInteger(input.targetPersonId)
    || !/^[a-f0-9]{64}$/.test(String(input.frozenInventorySha256 || ''))) return null;
  return {
    authUid: input.authUid,
    formerUserId: input.formerUserId,
    targetUserId: input.targetUserId,
    targetPersonId: input.targetPersonId,
    frozenInventorySha256: input.frozenInventorySha256 as string,
    ...(input.expectedBindingRevision !== undefined
      ? { expectedBindingRevision: input.expectedBindingRevision as number } : {}),
    ...(input.expectedBindingOperationDigest !== undefined
      ? { expectedBindingOperationDigest: input.expectedBindingOperationDigest as string } : {}),
  };
};

const AdminTasksPanel: React.FC = () => {
  const [tasks, setTasks] = useState<AdminTask[]>([]);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [openOnly, setOpenOnly] = useState(true);
  const [statuses, setStatuses] = useState<RegistrationStatus[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedReg, setSelectedReg] = useState<Registration | null>(null);
  const [selectedDetailsTask, setSelectedDetailsTask] = useState<AdminTask | null>(null);
  const [closingDetailsTask, setClosingDetailsTask] = useState(false);
  
  // Refund dialog state
  const [refundDialogOpen, setRefundDialogOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState<AdminTask | null>(null);
  const [selectedTaskReg, setSelectedTaskReg] = useState<Registration | null>(null);
  const [refundAmount, setRefundAmount] = useState('');
  const [adminComment, setAdminComment] = useState('');
  const [sendApproveEmail, setSendApproveEmail] = useState(true);
  const [sendDeclineEmail, setSendDeclineEmail] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Auto-link dialog state
  const [autoLinkDialogOpen, setAutoLinkDialogOpen] = useState(false);
  const [selectedAutoLinkTask, setSelectedAutoLinkTask] = useState<AdminTask | null>(null);
  const [autoLinkComment, setAutoLinkComment] = useState('');
  const [autoLinkProcessing, setAutoLinkProcessing] = useState(false);
  const [autoLinkError, setAutoLinkError] = useState<string | null>(null);

  // Claim/unclaim dialog state
  const [claimDialogOpen, setClaimDialogOpen] = useState(false);
  const [selectedClaimTask, setSelectedClaimTask] = useState<AdminTask | null>(null);
  const [claimComment, setClaimComment] = useState('');
  const [claimProcessing, setClaimProcessing] = useState(false);
  const [claimError, setClaimError] = useState<string | null>(null);

  const [accountBindingReviewTask, setAccountBindingReviewTask] = useState<AdminTask | null>(null);
  const [accountBindingReviewController, setAccountBindingReviewController] = useState<AccountBindingReviewController | null>(null);
  const [accountReviewKind, setAccountReviewKind] = useState<'binding' | 'unclaim'>('binding');
  const [unclaimPrepareTask, setUnclaimPrepareTask] = useState<AdminTask | null>(null);
  const [unclaimPrepareActor, setUnclaimPrepareActor] = useState<string | null>(null);
  const [bindingPrepareActor, setBindingPrepareActor] = useState<string | null>(null);
  const bindingPrepareTransport = useCallback(async (payload: unknown): Promise<unknown> => {
    if (!ACCOUNT_BINDING_REVIEW_FUNCTION) throw new Error('Account binding review is disabled.');
    return (await httpsCallable<unknown, unknown>(getFunctions(undefined, 'europe-west1'), ACCOUNT_BINDING_REVIEW_FUNCTION)(payload)).data;
  }, []);
  const unclaimPrepareTransport = useCallback(async (payload: unknown): Promise<unknown> => {
    if (!REVIEWED_UNCLAIM_FUNCTION) throw new Error('Reviewed unclaim is disabled.');
    return (await httpsCallable<unknown, unknown>(getFunctions(undefined, 'europe-west1'), REVIEWED_UNCLAIM_FUNCTION)(payload)).data;
  }, []);
  const [accountBindingReviewLoadingTaskId, setAccountBindingReviewLoadingTaskId] = useState<string | null>(null);
  const [accountBindingReviewError, setAccountBindingReviewError] = useState<string | null>(null);
  const accountReviewRequest = useRef(0);
  useEffect(() => () => { accountReviewRequest.current += 1; }, []);

  const isCancellationReviewTask = (
    task: AdminTask | null | undefined
  ): task is AdminTask & { type: 'registration_cancellation_review' } =>
    Boolean(task && task.type === 'registration_cancellation_review');

  const isCancellationReviewed = (task: AdminTask | null | undefined): boolean =>
    Boolean(task?.cancellationReviewedAt || task?.cancellationDecision);

  const isRefundReviewed = (task: AdminTask | null | undefined): boolean =>
    Boolean(task?.refundReviewedAt || task?.refundDecision);

  const requiresRefundReview = (task: AdminTask | null | undefined, reg?: Registration | null): boolean => {
    if (!task) return false;
    if (typeof task.requiresRefundReview === 'boolean') return task.requiresRefundReview;
    const descriptionPaymentMade = Number((task.description as any)?.paymentMade ?? 0);
    if (descriptionPaymentMade > 0) return true;
    return Number(reg?.paymentMade || 0) > 0;
  };

  const shouldResolveCancellationTask = (
    task: AdminTask,
    options?: {
      cancellationReviewed?: boolean;
      refundReviewed?: boolean;
      requiresRefund?: boolean;
      registration?: Registration | null;
    }
  ): boolean => {
    const cancellationReviewed = options?.cancellationReviewed ?? isCancellationReviewed(task);
    const refundReviewed = options?.refundReviewed ?? isRefundReviewed(task);
    const refundRequired = options?.requiresRefund ?? requiresRefundReview(task, options?.registration);
    return cancellationReviewed && (!refundRequired || refundReviewed);
  };

  useEffect(() => {
    const unsub = onSnapshot(collection(db, 'adminTasks'), snapshot => {
      const arr: AdminTask[] = snapshot.docs.map(doc => {
        const d = doc.data() as any;
        return {
          registrationId: d.registrationId,
          id: doc.id,
          type: d.type,
          status: d.status,
          description: d.description,
          link: d.link,
          createdAt: d.createdAt?.toDate(),
          operationKey: d.operationKey,
          proposedBy: d.proposedBy,
          // Auto-link fields
          authUid: d.authUid,
          authEmail: d.authEmail,
          authDisplayName: d.authDisplayName,
          newUserDocId: d.newUserDocId,
          existingDocId: d.existingDocId,
          existingDisplayName: d.existingDisplayName,
          existingFirstName: d.existingFirstName,
          existingLastName: d.existingLastName,
          existingEmail: d.existingEmail,
          existingPersonId: d.existingPersonId,
          existingYearOfBirth: d.existingYearOfBirth,
          existingNationality: d.existingNationality,
          matchType: d.matchType,
          adminComment: d.adminComment,
          // Claim/unclaim fields
          requesterUid: d.requesterUid,
          requesterEmail: d.requesterEmail,
          requesterName: d.requesterName,
          targetProfileDocId: d.targetProfileDocId,
          targetPersonId: d.targetPersonId,
          targetName: d.targetName,
          targetEmail: d.targetEmail,
          targetYearOfBirth: d.targetYearOfBirth,
          targetNationality: d.targetNationality,
          profileDocId: d.profileDocId,
          personId: d.personId,
          profileName: d.profileName,
          profileEmail: d.profileEmail,
          reason: d.reason,
          reviewProtocol: d.reviewProtocol,
          requiresRefundReview: d.requiresRefundReview,
          cancellationReviewedAt: d.cancellationReviewedAt?.toDate?.(),
          cancellationDecision: d.cancellationDecision,
          refundReviewedAt: d.refundReviewedAt?.toDate?.(),
          refundDecision: d.refundDecision,
        };
      });
      setTasks(arr);
      setSelectedDetailsTask((current) => {
        if (!current?.id) return current;
        const latest = arr.find((task) => task.id === current.id);
        return latest || current;
      });
    });
    return () => unsub();
  }, []);

  const openAccountBindingReview = async (task: AdminTask) => {
    const actorUid = auth.currentUser?.uid;
    const unclaim = task.type === 'unclaim_profile' && task.reviewProtocol === 'reviewed-unclaim-v1';
    const route = unclaim ? REVIEWED_UNCLAIM_FUNCTION : ACCOUNT_BINDING_REVIEW_FUNCTION;
    if (!route || (!unclaim && task.type !== 'review_account_binding') || task.status !== 'open'
      || !actorUid || task.proposedBy !== actorUid || !task.operationKey) return;
    const requestId = ++accountReviewRequest.current;
    const isCurrent = () => accountReviewRequest.current === requestId && auth.currentUser?.uid === actorUid;
    setAccountBindingReviewLoadingTaskId(task.id);
    setAccountBindingReviewError(null);
    try {
      const functions = getFunctions(undefined, 'europe-west1');
      const call = httpsCallable<unknown, unknown>(functions, route);
      const initial = await call({ phase: 'preview', taskId: task.id });
      if (!isCurrent()) return;
      const preview = initial.data;
      if (!isPlainRecord(preview) || preview.phase !== 'preview' || preview.taskId !== task.id
        || preview.operationKey !== task.operationKey || preview.validation !== 'current-state'
        || preview.readinessVerified !== true || preview.applied !== false) {
        throw new Error('Invalid account review preview.');
      }
      const input = preview.input;
      if (!isPlainRecord(input) || input.taskId !== task.id || input.operationKey !== task.operationKey) {
        throw new Error('Invalid account review input.');
      }
      const transport = async (payload: unknown) => {
        const response = (await call(payload)).data;
        if (!isPlainRecord(response) || response.taskId !== task.id) {
          throw new Error('Account review response task mismatch.');
        }
        return response;
      };
      let controller: AccountBindingReviewController;
      if (unclaim) {
        controller = createReviewedUnclaimReviewController(transport, { taskId: task.id,
          operationKey: task.operationKey, frozenInventorySha256: input.frozenInventorySha256 as string });
      } else {
        const selection = accountBindingSelectionFromInput(input);
        if (!selection) throw new Error('Invalid account review selection.');
        controller = createAccountBindingReviewController(transport, task.operationKey, selection);
      }
      setAccountReviewKind(unclaim ? 'unclaim' : 'binding');
      setAccountBindingReviewTask(task);
      setAccountBindingReviewController(controller);
    } catch {
      if (isCurrent()) setAccountBindingReviewError('Could not open this account binding review. Refresh the task list and try again.');
    } finally {
      if (isCurrent()) setAccountBindingReviewLoadingTaskId(null);
    }
  };

  const closeAccountBindingReview = () => {
    accountReviewRequest.current += 1;
    setAccountBindingReviewTask(null);
    setAccountBindingReviewController(null);
  };

  const closeSelectedDetailsTask = async () => {
    if (!selectedDetailsTask?.id) {
      return;
    }

    setClosingDetailsTask(true);
    try {
      if (isCancellationReviewTask(selectedDetailsTask)) {
        const shouldResolve = shouldResolveCancellationTask(selectedDetailsTask, {
          cancellationReviewed: true,
          registration: selectedReg,
        });

        const updates: Record<string, any> = {
          cancellationReviewedAt: serverTimestamp(),
          cancellationDecision: 'closed',
          updatedAt: serverTimestamp(),
        };

        if (shouldResolve) {
          updates.status = 'resolved';
          updates.resolvedAt = serverTimestamp();
        }

        await updateDoc(doc(db, 'adminTasks', selectedDetailsTask.id), updates);
      } else {
        await updateDoc(doc(db, 'adminTasks', selectedDetailsTask.id), {
          status: 'resolved',
          resolvedAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
      }
    } catch (err) {
      console.error('[AdminTasksPanel] Error resolving cancellation review task:', err);
    } finally {
      setClosingDetailsTask(false);
    }
  };

  useEffect(() => {
    // Get registration statuses from codeLists collection
    listCodeList('status', 'registrations')
      .then(items => {
        // Map to the expected RegistrationStatus interface
        const statuses = items.map(item => ({
          id: item.id,
          label: item.code
        }));
        setStatuses(statuses);
      })
      .catch(console.error);
  }, []);

  const parseDateNb = (s: string): Date | null => {
    const [d, m, y] = s.split(/[./]/).map(n => parseInt(n, 10));
    return isNaN(d)||isNaN(m)||isNaN(y) ? null : new Date(y, m-1, d);
  };

  const filteredTasks = tasks.filter(t => {
    if (openOnly && t.status !== 'open') return false;
    if (fromDate) {
      const from = parseDateNb(fromDate);
      if (!from || !t.createdAt || t.createdAt < from) return false;
    }
    if (toDate) {
      const to = parseDateNb(toDate);
      if (!to || !t.createdAt || t.createdAt > to) return false;
    }
    return true;
  });

  const openDetails = async (regId: string, task?: AdminTask) => {
    if (!regId) {
      console.warn('[AdminTasksPanel] No registrationId provided');
      return;
    }
    try {
      const reg = await getRegistrationById(regId);
      if (reg) {
        setSelectedReg(reg);
        setSelectedDetailsTask(task || null);
        setDialogOpen(true);
      } else {
        console.warn('[AdminTasksPanel] Registration not found:', regId);
      }
    } catch (err) {
      console.error('[AdminTasksPanel] Error fetching registration:', err);
    }
  };

  // Resolve email template variables task
  const resolveEmailTemplateTask = async (task: AdminTask) => {
    if (!task.id) return;
    
    try {
      await updateDoc(doc(db, 'adminTasks', task.id), {
        status: 'resolved',
        resolvedAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      console.log('[AdminTasksPanel] Email template task resolved:', task.id);
    } catch (err) {
      console.error('[AdminTasksPanel] Error resolving email template task:', err);
    }
  };

  // Open refund dialog with task and registration details
  const openRefundDialog = async (task: AdminTask) => {
    setSelectedTask(task);
    setAdminComment(task.adminComment || '');
    setError(null);
    setSendApproveEmail(true);
    setSendDeclineEmail(true);
    setRefundAmount('');
    
    if (task.registrationId) {
      try {
        const reg = await getRegistrationById(task.registrationId);
        setSelectedTaskReg(reg);
        if (reg) {
          const paymentMade = Number(reg.paymentMade || 0);
          const paymentRequired = Number(reg.paymentRequired || 0);
          const defaultAmount =
            task.type === 'registration_cancellation_review'
              ? paymentMade
              : Math.max(paymentMade - paymentRequired, 0);
          setRefundAmount(defaultAmount > 0 ? String(defaultAmount) : '');
        }
      } catch (err) {
        console.error('[AdminTasksPanel] Error fetching registration for refund:', err);
        setSelectedTaskReg(null);
      }
    }
    setRefundDialogOpen(true);
  };

  // Handle refund approval
  const handleApproveRefund = async () => {
    if (!selectedTask || !selectedTaskReg) return;
    
    setProcessing(true);
    setError(null);
    
    try {
      // Get Stripe payment info from registration
      const payments = selectedTaskReg.payments || [];
      const stripePayments = payments.filter((p: any) => p.method === 'stripe' && p.chargeId);
      const parsedRefundAmount = Number(refundAmount);
      const maxRefundable = Number(selectedTaskReg.paymentMade || 0);
      if (!Number.isFinite(parsedRefundAmount) || parsedRefundAmount <= 0) {
        throw new Error('Refund amount must be greater than 0.');
      }
      if (parsedRefundAmount > maxRefundable) {
        throw new Error(`Refund amount cannot exceed payment made (${maxRefundable} NOK).`);
      }
      
      // For Stripe refunds, the payment entry will be created by the Cloud Function
      // For manual refunds, create the payment entry here
      if (stripePayments.length === 0) {
        const refundPaymentEntry = {
          date: new Date(),
          amount: -parsedRefundAmount, // Negative amount for refund
          method: 'manual_refund',
          note: adminComment || 'Refund approved',
          adminTaskId: selectedTask.id
        };
        
        // Update registration with negative payment and adjusted paymentMade
        const newPaymentMade = (selectedTaskReg.paymentMade || 0) - parsedRefundAmount;
        await updateDoc(doc(db, 'registrations', selectedTask.registrationId), {
          payments: arrayUnion(refundPaymentEntry),
          paymentMade: newPaymentMade,
          updatedAt: serverTimestamp()
        });
        console.log('[AdminTasksPanel] Added manual refund payment entry to registration', selectedTask.registrationId);
      }
      
      if (stripePayments.length === 0) {
        // No Stripe payments - inform admin to process manually
        if (isCancellationReviewTask(selectedTask)) {
          const shouldResolve = shouldResolveCancellationTask(selectedTask, {
            refundReviewed: true,
            registration: selectedTaskReg,
          });

          const updates: Record<string, any> = {
            adminComment: adminComment,
            sendApproveEmail,
            refundDecision: 'approved_manual',
            refundReviewedAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
            refundResult: {
              type: 'manual',
              message: 'No Stripe payments found. Refund must be processed manually.',
              paymentMethods: payments.map((p: any) => p.method),
            },
          };

          if (shouldResolve) {
            updates.status = 'resolved';
            updates.resolvedAt = serverTimestamp();
          }

          await updateDoc(doc(db, 'adminTasks', selectedTask.id), updates);
        } else {
          await updateDoc(doc(db, 'adminTasks', selectedTask.id), {
            status: 'approved_manual',
            adminComment: adminComment,
            sendApproveEmail,
            resolvedAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
            refundResult: {
              type: 'manual',
              message: 'No Stripe payments found. Refund must be processed manually.',
              paymentMethods: payments.map((p: any) => p.method)
            }
          });
        }
        
        // Send refund_validated email if enabled
        if (sendApproveEmail) {
          try {
            await sendEmail(EmailType.REFUND_VALIDATED, selectedTaskReg.email, {
              ...selectedTaskReg,
              refundAmount: parsedRefundAmount,
              refundMethod: 'manual',
              adminComment: adminComment || 'Your refund has been approved and will be processed manually.'
            });
            console.log('[AdminTasksPanel] Sent refund_validated email');
          } catch (emailErr) {
            console.error('[AdminTasksPanel] Failed to send refund_validated email:', emailErr);
          }
        }
        
        alert(
          sendApproveEmail
            ? 'Refund approved. No Stripe payments found - please process refund manually. Email sent to user.'
            : 'Refund approved. No Stripe payments found - please process refund manually.'
        );
      } else {
        // Has Stripe payments - call Cloud Function to process refund
        try {
          console.log('[AdminTasksPanel] Calling processStripeRefund Cloud Function');
          const functions = getFunctions(undefined, 'europe-west1');
          const processRefund = httpsCallable(functions, REFUND_FUNCTIONS.process);
          
          const refundRequest = {
            adminTaskId: selectedTask.id,
            registrationId: selectedTask.registrationId,
            refundAmount: parsedRefundAmount,
            chargeIds: stripePayments.map((p: any) => p.chargeId).filter(Boolean),
            reason: adminComment || 'Refund approved by admin',
            notifyParticipant: sendApproveEmail,
            approvedEmailTemplate: 'refund_validated',
          };
          
          const result = await processRefund(refundRequest);
          const refundResponse = result.data as any;
          
          console.log('[AdminTasksPanel] Stripe refund result:', refundResponse);

          const emailNotification = refundResponse?.emailNotification;
          const attemptedTemplates = Array.isArray(emailNotification?.attemptedTemplates)
            ? emailNotification.attemptedTemplates.join(', ')
            : '';
          const emailMessage = sendApproveEmail
            ? emailNotification
              ? emailNotification.queued
                ? `\n\nEmail queued (${emailNotification.templateUsed || 'refund_validated'})`
                : `\n\nRefund succeeded, but email was not queued: ${emailNotification.error || 'No error details returned'}${attemptedTemplates ? `\nTemplates tried: ${attemptedTemplates}` : ''}`
              : '\n\nRefund succeeded, but function returned no email diagnostics. Confirm latest functions are deployed.'
            : '';
          
          if (refundResponse.success) {
            alert(
              `Refund successful! ${refundResponse.message}\n\nRefunded ${refundResponse.totalRefunded} NOK via Stripe.${emailMessage}`
            );
          } else {
            // Partial refund or errors
            alert(`Refund partially completed: ${refundResponse.message}\n\nRefunded ${refundResponse.totalRefunded} NOK of ${parsedRefundAmount} NOK requested.${emailMessage}\n\nPlease review the admin task for details.`);
          }
        } catch (refundError: any) {
          console.error('[AdminTasksPanel] Stripe refund failed:', refundError);
          
          // Update task with error status
          if (isCancellationReviewTask(selectedTask)) {
            await updateDoc(doc(db, 'adminTasks', selectedTask.id), {
              adminComment: adminComment,
              refundDecision: 'approved_error',
              updatedAt: serverTimestamp(),
              refundResult: {
                type: 'stripe',
                error: refundError.message || 'Unknown error',
                message: 'Stripe refund failed - manual processing required'
              }
            });
          } else {
            await updateDoc(doc(db, 'adminTasks', selectedTask.id), {
              status: 'approved_error',
              adminComment: adminComment,
              updatedAt: serverTimestamp(),
              refundResult: {
                type: 'stripe',
                error: refundError.message || 'Unknown error',
                message: 'Stripe refund failed - manual processing required'
              }
            });
          }
          
          alert(`Stripe refund failed: ${refundError.message}\n\nThe task has been marked for manual processing. Please process the refund manually via Stripe dashboard.`);
        }
      }
      
      setRefundDialogOpen(false);
      setSelectedTask(null);
      setSelectedTaskReg(null);
      setRefundAmount('');
      setSendApproveEmail(true);
    } catch (err: any) {
      console.error('[AdminTasksPanel] Error approving refund:', err);
      setError(err.message || 'Failed to approve refund');
    } finally {
      setProcessing(false);
    }
  };

  // Open auto-link review dialog
  const openAutoLinkDialog = (task: AdminTask) => {
    setSelectedAutoLinkTask(task);
    setAutoLinkComment('');
    setAutoLinkError(null);
    setAutoLinkDialogOpen(true);
  };

  // Handle auto-link approval
  const handleApproveAutoLink = async () => {
    if (!selectedAutoLinkTask) return;
    
    setAutoLinkProcessing(true);
    setAutoLinkError(null);
    
    try {
      const functions = getFunctions();
      const processAutoLink = httpsCallable(functions, ADMIN_FUNCTIONS.autoLink);
      
      const result = await processAutoLink({
        taskId: selectedAutoLinkTask.id,
        action: 'approve',
        adminComment: autoLinkComment || undefined
      });
      
      const response = result.data as any;
      console.log('[AdminTasksPanel] Auto-link approved:', response);
      
      setAutoLinkDialogOpen(false);
      setSelectedAutoLinkTask(null);
    } catch (err: any) {
      console.error('[AdminTasksPanel] Error approving auto-link:', err);
      setAutoLinkError(err.message || 'Failed to approve link');
    } finally {
      setAutoLinkProcessing(false);
    }
  };

  // Handle auto-link rejection
  const handleRejectAutoLink = async () => {
    if (!selectedAutoLinkTask) return;
    
    setAutoLinkProcessing(true);
    setAutoLinkError(null);
    
    try {
      const functions = getFunctions();
      const processAutoLink = httpsCallable(functions, ADMIN_FUNCTIONS.autoLink);
      
      const result = await processAutoLink({
        taskId: selectedAutoLinkTask.id,
        action: 'reject',
        adminComment: autoLinkComment || undefined
      });
      
      const response = result.data as any;
      console.log('[AdminTasksPanel] Auto-link rejected:', response);
      
      setAutoLinkDialogOpen(false);
      setSelectedAutoLinkTask(null);
    } catch (err: any) {
      console.error('[AdminTasksPanel] Error rejecting auto-link:', err);
      setAutoLinkError(err.message || 'Failed to reject link');
    } finally {
      setAutoLinkProcessing(false);
    }
  };

  // Open claim/unclaim review dialog
  const openClaimDialog = (task: AdminTask) => {
    setSelectedClaimTask(task);
    setClaimComment('');
    setClaimError(null);
    setClaimDialogOpen(true);
  };

  // Handle claim/unclaim approval
  const handleApproveClaim = async () => {
    if (!selectedClaimTask) return;
    if (selectedClaimTask.type === 'claim_profile' && isStrictIdentityPolicy()) {
      setClaimError('This claim request is advisory in strict identity mode. Use the controlled identity review before changing account ownership.');
      return;
    }
    
    setClaimProcessing(true);
    setClaimError(null);
    
    try {
      const functions = getFunctions(undefined, 'europe-west1');
      const processClaimFn = httpsCallable(functions, ADMIN_FUNCTIONS.claim);
      
      const result = await processClaimFn({
        taskId: selectedClaimTask.id,
        action: 'approve',
        notes: claimComment || undefined
      });
      
      const response = result.data as any;
      console.log('[AdminTasksPanel] Claim task approved:', response);
      
      setClaimDialogOpen(false);
      setSelectedClaimTask(null);
    } catch (err: any) {
      console.error('[AdminTasksPanel] Error approving claim:', err);
      setClaimError(err.message || 'Failed to approve claim');
    } finally {
      setClaimProcessing(false);
    }
  };

  // Handle claim/unclaim rejection
  const handleRejectClaim = async () => {
    if (!selectedClaimTask) return;
    
    setClaimProcessing(true);
    setClaimError(null);
    
    try {
      const functions = getFunctions(undefined, 'europe-west1');
      const processClaimFn = httpsCallable(functions, ADMIN_FUNCTIONS.claim);
      
      const result = await processClaimFn({
        taskId: selectedClaimTask.id,
        action: 'reject',
        notes: claimComment || undefined
      });
      
      const response = result.data as any;
      console.log('[AdminTasksPanel] Claim task rejected:', response);
      
      setClaimDialogOpen(false);
      setSelectedClaimTask(null);
    } catch (err: any) {
      console.error('[AdminTasksPanel] Error rejecting claim:', err);
      setClaimError(err.message || 'Failed to reject claim');
    } finally {
      setClaimProcessing(false);
    }
  };

  // Handle refund decline
  const handleDeclineRefund = async () => {
    if (!selectedTask || !selectedTaskReg) return;
    
    setProcessing(true);
    setError(null);
    
    try {
      await updateDoc(doc(db, 'adminTasks', selectedTask.id), {
        ...(isCancellationReviewTask(selectedTask)
          ? {
              adminComment: adminComment,
              refundDecision: 'declined',
              refundReviewedAt: serverTimestamp(),
              updatedAt: serverTimestamp(),
              sendDeclineEmail: sendDeclineEmail,
              ...(shouldResolveCancellationTask(selectedTask, {
                refundReviewed: true,
                registration: selectedTaskReg,
              })
                ? { status: 'resolved', resolvedAt: serverTimestamp() }
                : {}),
            }
          : {
              status: 'declined',
              adminComment: adminComment,
              resolvedAt: serverTimestamp(),
              updatedAt: serverTimestamp(),
              sendDeclineEmail: sendDeclineEmail,
            })
      });
      
      if (sendDeclineEmail) {
        // Send refund_declined email
        try {
          const declineAmount = Number(refundAmount) > 0
            ? Number(refundAmount)
            : (selectedTaskReg.paymentMade || 0) - (selectedTaskReg.paymentRequired || 0);
          await sendEmail(EmailType.REFUND_DECLINED, selectedTaskReg.email, {
            ...selectedTaskReg,
            refundAmount: declineAmount,
            adminComment: adminComment || 'Your refund request has been reviewed and declined.'
          });
          console.log('[AdminTasksPanel] Sent refund_declined email');
        } catch (emailErr) {
          console.error('[AdminTasksPanel] Failed to send refund_declined email:', emailErr);
        }
      }
      
      setRefundDialogOpen(false);
      setSelectedTask(null);
      setSelectedTaskReg(null);
      setRefundAmount('');
    } catch (err: any) {
      console.error('[AdminTasksPanel] Error declining refund:', err);
      setError(err.message || 'Failed to decline refund');
    } finally {
      setProcessing(false);
    }
  };

  // Format payment for display
  const formatPayment = (payment: any) => {
    const date = payment.date?.toDate?.() || new Date(payment.date);
    const dateStr = date.toLocaleDateString('nb-NO', { day: '2-digit', month: '2-digit', year: 'numeric' });
    const method = payment.paymentMethodType || payment.method || 'unknown';
    const cardInfo = payment.paymentMethodDetails?.card 
      ? ` (${payment.paymentMethodDetails.card.brand} ****${payment.paymentMethodDetails.card.last4})`
      : '';
    return `${dateStr}: ${payment.amount} NOK via ${method}${cardInfo}`;
  };

  const formatReasonCode = (value: unknown): string => {
    const normalized = String(value || '').trim().toLowerCase();
    if (!normalized || normalized === 'noreason' || normalized === 'noreasongiven') {
      return 'No reason provided';
    }
    return normalized.replace(/_/g, ' ');
  };

  const formatCancellationTaskDescription = (task: AdminTask): string => {
    const d = (task.description || {}) as any;
    const runnerName = `${d.firstName || ''} ${d.lastName || ''}`.trim() || 'Unknown runner';
    const fromStatus = String(d.previousStatus || 'unknown').toLowerCase();
    const toStatus = String(d.newStatus || 'cancelled').toLowerCase();
    const paymentMade = Number(d.paymentMade || 0);
    const paymentRequired = Number(d.paymentRequired || 0);
    const reasonCode = formatReasonCode(d.reasonCode);
    const reasonText = String(d.reasonText || '').trim();
    const source = String(d.source || '').trim() === 'self_service' ? 'Self-service' : 'Admin';

    const parts = [
      `${runnerName} (Reg# ${d.registrationNumber || 'N/A'})`,
      `Edition: ${d.editionId || 'N/A'}`,
      `Race: ${d.raceDistance || 'N/A'}`,
      `Status: ${fromStatus} -> ${toStatus}`,
      `Payment: ${paymentMade} / ${paymentRequired} NOK`,
      `Reason: ${reasonCode}${reasonText ? ` (${reasonText})` : ''}`,
      `Source: ${source}`,
      d.email ? `Email: ${d.email}` : null,
    ].filter(Boolean);

    return parts.join(' | ');
  };

  return (
    <>
      <Typography variant="h4" gutterBottom>Admin Tasks</Typography>
      {ACCOUNT_BINDING_REVIEW_FUNCTION && !!auth.currentUser?.uid && (
        <Button variant="outlined" sx={{ mb: 2 }} onClick={() => setBindingPrepareActor(auth.currentUser?.uid || null)}>
          Prepare account binding review
        </Button>
      )}
      <Box mb={2} display="flex" alignItems="center" gap={2}>
        <TextField label="From" type="text" placeholder="dd.mm.yyyy" value={fromDate} onChange={e => setFromDate(e.target.value)} size="small" InputLabelProps={{shrink:true}} />
        <TextField label="To" type="text" placeholder="dd.mm.yyyy" value={toDate} onChange={e => setToDate(e.target.value)} size="small" InputLabelProps={{shrink:true}} />
        <FormControlLabel control={<Checkbox checked={openOnly} onChange={e => setOpenOnly(e.target.checked)} />} label="Open Only" />
      </Box>
      <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Status</TableCell>
              <TableCell sx={{minWidth:400}}>Description</TableCell>
              <TableCell>Link</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredTasks.map(t => (
              <TableRow key={t.id}>
                <TableCell>
                  {t.createdAt ? t.createdAt.toLocaleDateString('nb-NO',{day:'2-digit',month:'2-digit',year:'numeric'}) : ''}
                </TableCell>
                <TableCell>{t.type}</TableCell>
                <TableCell>{t.status}</TableCell>
                <TableCell>
                  {t.type === 'refund' && t.description
                    ? `Edition: ${t.description.editionId} / Reg#: ${t.description.registrationNumber} – ${t.description.firstName} ${t.description.lastName}, paid: ${t.description.paymentsMade}, list: ${t.description.list}`
                    : t.type === 'email-template-variables' && t.description
                      ? `Template: ${t.description.emailType} (${t.description.usedLocale})<br/>Missing: ${t.description.missingVariables?.join(', ') || 'N/A'}<br/>Recipient: ${t.description.recipientEmail}`
                      : t.type === 'registration_cancellation_review'
                        ? formatCancellationTaskDescription(t)
                      : t.type === 'auto_link_pending'
                        ? `New user ${t.authEmail || 'N/A'} → Profile: ${t.existingDisplayName || t.existingFirstName || 'Unknown'}${t.existingPersonId ? ` (ID: ${t.existingPersonId})` : ''}`
                      : t.type === 'review_account_binding'
                        ? 'Strict account binding review'
                      : t.type === 'claim_profile'
                          ? `"${t.requesterName || t.requesterEmail || 'Unknown'}" claims → "${t.targetName || 'Unknown'}"${t.targetPersonId ? ` (ID: ${t.targetPersonId})` : ''}`
                          : t.type === 'unclaim_profile'
                            ? `"${t.requesterName || t.requesterEmail || 'Unknown'}" wants to unlink from "${t.profileName || 'Unknown'}"${t.personId ? ` (ID: ${t.personId})` : ''}`
                            : t.description
                              ? JSON.stringify(t.description)
                              : ''}
                </TableCell>
                <TableCell>
                  <Box display="flex" gap={1}>
                    {t.registrationId && (
                      <Button size="small" variant="outlined" onClick={() => openDetails(t.registrationId, t)}>View Reg</Button>
                    )}
                    {(t.type === 'refund' ||
                      (t.type === 'registration_cancellation_review' &&
                        t.status === 'open' &&
                        requiresRefundReview(t) &&
                        !isRefundReviewed(t))) && (
                      <Button size="small" variant="contained" color="primary" onClick={() => openRefundDialog(t)}>Review</Button>
                    )}
                    {t.type === 'email-template-variables' && t.status === 'open' && (
                      <Button size="small" variant="contained" color="secondary" onClick={() => resolveEmailTemplateTask(t)}>Mark Resolved</Button>
                    )}
                    {t.type === 'auto_link_pending' && t.status === 'open' && (
                      <Button size="small" variant="contained" color="info" onClick={() => openAutoLinkDialog(t)}>Review Link</Button>
                    )}
                    {((ACCOUNT_BINDING_REVIEW_FUNCTION && t.type === 'review_account_binding')
                      || (REVIEWED_UNCLAIM_FUNCTION && t.type === 'unclaim_profile' && t.reviewProtocol === 'reviewed-unclaim-v1'))
                      && t.status === 'open'
                      && !!auth.currentUser?.uid && t.proposedBy === auth.currentUser.uid && (
                      <Button
                        size="small"
                        variant="contained"
                        color="info"
                        onClick={() => openAccountBindingReview(t)}
                        disabled={accountBindingReviewLoadingTaskId !== null}
                      >
                        {accountBindingReviewLoadingTaskId === t.id
                          ? <CircularProgress size={18} aria-label="Opening account review" />
                          : t.type === 'unclaim_profile' ? 'Review new-person unclaim' : 'Review account binding'}
                      </Button>
                    )}
                    {(t.type === 'claim_profile' || t.type === 'unclaim_profile') && t.status === 'open' && !t.reviewProtocol
                      && !(REVIEWED_UNCLAIM_FUNCTION && t.type === 'unclaim_profile') && (
                      <Button size="small" variant="contained" color="secondary" onClick={() => openClaimDialog(t)}>Review Claim</Button>
                    )}
                    {REVIEWED_UNCLAIM_FUNCTION && t.type === 'unclaim_profile' && t.status === 'open'
                      && !t.reviewProtocol && !!auth.currentUser?.uid && (
                      <Button size="small" variant="outlined" onClick={() => {
                        setUnclaimPrepareActor(auth.currentUser?.uid || null);
                        setUnclaimPrepareTask(t);
                      }}>Prepare reviewed unclaim</Button>
                    )}
                    {t.type === 'refund' && t.status !== 'open' && (
                      <Chip size="small" label={t.status} color={t.status === 'declined' ? 'error' : 'success'} />
                    )}
                    {t.type === 'email-template-variables' && t.status !== 'open' && (
                      <Chip size="small" label={t.status} color="success" />
                    )}
                    {t.type === 'auto_link_pending' && t.status !== 'open' && (
                      <Chip size="small" label={t.status} color={t.status === 'rejected' ? 'error' : 'success'} />
                    )}
                    {(t.type === 'claim_profile' || t.type === 'unclaim_profile') && t.status !== 'open' && (
                      <Chip size="small" label={t.status} color={t.status === 'rejected' ? 'error' : 'success'} />
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {accountBindingReviewError && (
        <Alert severity="error" sx={{ mt: 2 }}>{accountBindingReviewError}</Alert>
      )}
      {selectedReg && (
        <RegistrationDetailsDialog
          open={dialogOpen}
          registration={selectedReg}
          statuses={statuses}
          linkedAdminTaskId={selectedDetailsTask?.id || null}
          linkedAdminTaskOpen={selectedDetailsTask?.status === 'open'}
          onClose={() => {
            setDialogOpen(false);
            setSelectedReg(null);
            setSelectedDetailsTask(null);
          }}
          onUpdate={() => {}}
          footerActions={
            selectedDetailsTask?.type === 'registration_cancellation_review' &&
            selectedDetailsTask?.status === 'open' &&
            !isCancellationReviewed(selectedDetailsTask) ? (
              <Button
                variant="contained"
                color="success"
                onClick={closeSelectedDetailsTask}
                disabled={closingDetailsTask}
              >
                {closingDetailsTask ? 'Closing…' : 'Close Task'}
              </Button>
            ) : undefined
          }
        />
      )}

      {/* Refund Review Dialog */}
      <Dialog
        open={refundDialogOpen}
        onClose={() => {
          if (processing) return;
          setRefundDialogOpen(false);
          setRefundAmount('');
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Review Refund Request</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          
          {selectedTask && (
            <Box>
              {/* Task Info */}
              <Typography variant="subtitle2" color="text.secondary">Task ID: {selectedTask.id}</Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Created: {selectedTask.createdAt?.toLocaleDateString('nb-NO', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
              </Typography>
              
              <Divider sx={{ my: 2 }} />
              
              {/* Registration Info */}
              {selectedTaskReg ? (
                <>
                  <Typography variant="h6" gutterBottom>Registration Details</Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mb: 2 }}>
                    <Typography><strong>Name:</strong> {selectedTaskReg.firstName} {selectedTaskReg.lastName}</Typography>
                    <Typography><strong>Email:</strong> {selectedTaskReg.email}</Typography>
                    <Typography><strong>Edition:</strong> {selectedTaskReg.editionId}</Typography>
                    <Typography><strong>Reg #:</strong> {selectedTaskReg.registrationNumber}</Typography>
                    <Typography><strong>Status:</strong> {selectedTaskReg.status}</Typography>
                    <Typography><strong>Race:</strong> {selectedTaskReg.raceDistance}</Typography>
                  </Box>
                  
                  <Divider sx={{ my: 2 }} />
                  
                  {/* Payment Info */}
                  <Typography variant="h6" gutterBottom>Payment Information</Typography>
                  <Box sx={{ mb: 2 }}>
                    <Typography><strong>Payment Required:</strong> {selectedTaskReg.paymentRequired || 0} NOK</Typography>
                    <Typography><strong>Payment Made:</strong> {selectedTaskReg.paymentMade || 0} NOK</Typography>
                    <TextField
                      type="number"
                      size="small"
                      label="Refund Amount (NOK)"
                      value={refundAmount}
                      onChange={(e) => setRefundAmount(e.target.value)}
                      inputProps={{ min: 0, step: '0.01' }}
                      sx={{ mt: 1, maxWidth: 260 }}
                    />
                  </Box>
                  
                  {/* Change Information */}
                  {(selectedTask.description?.licenseChanged || selectedTask.description?.raceDistanceChanged) && (
                    <Box sx={{ mb: 2, p: 2, bgcolor: 'warning.light', borderRadius: 1, color: 'warning.dark' }}>
                      <Typography variant="subtitle2" gutterBottom color="inherit"><strong>Registration Changes</strong></Typography>
                      
                      {selectedTask.description?.raceDistanceChanged && (
                        <Typography variant="body2" sx={{ mb: 1 }} color="inherit">
                          <strong>Race Distance Changed:</strong> {selectedTask.description.previousRaceDistance || 'N/A'} → {selectedTaskReg.raceDistance}
                          {selectedTask.description.paymentRequiredChange && (
                            <span style={{ color: selectedTask.description.paymentRequiredChange < 0 ? 'green' : 'red' }}>
                              {' '}({selectedTask.description.paymentRequiredChange > 0 ? '+' : ''}{selectedTask.description.paymentRequiredChange} NOK)
                            </span>
                          )}
                        </Typography>
                      )}
                      
                      {selectedTask.description?.licenseChanged && (
                        <Typography variant="body2" color="inherit">
                          <strong>License Changed:</strong> {selectedTask.description.previousHasYearLicense ? 'Year License → One-day License' : 'One-day License → Year License'}
                          {selectedTask.description.licenseCostDifference > 0 && ` (Refund includes ${selectedTask.description.licenseCostDifference} NOK)`}
                        </Typography>
                      )}
                    </Box>
                  )}
                  
                  {/* License Info (if no changes) */}
                  {!selectedTask.description?.licenseChanged && selectedTaskReg.hasYearLicense !== undefined && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2">
                        <strong>License:</strong> {selectedTaskReg.hasYearLicense ? 'Year License' : 'One-day License (30 NOK)'}
                        {selectedTaskReg.licenseNumber && ` - #${selectedTaskReg.licenseNumber}`}
                      </Typography>
                    </Box>
                  )}
                  
                  {/* Payment History */}
                  {selectedTaskReg.payments && selectedTaskReg.payments.length > 0 && (
                    <>
                      <Typography variant="subtitle1" gutterBottom>Payment History</Typography>
                      <Box sx={{ 
                        bgcolor: (theme) => theme.palette.mode === 'dark' ? 'grey.800' : 'grey.100', 
                        p: 2, 
                        borderRadius: 1, 
                        mb: 2 
                      }}>
                        {selectedTaskReg.payments.map((p: any, idx: number) => (
                          <Box key={idx} sx={{ mb: 1 }}>
                            <Typography variant="body2" color="text.primary">{formatPayment(p)}</Typography>
                            {p.chargeId && (
                              <Typography variant="caption" color="text.secondary">
                                Charge ID: {p.chargeId}
                              </Typography>
                            )}
                          </Box>
                        ))}
                      </Box>
                    </>
                  )}
                  
                  {/* Stripe Info */}
                  {selectedTaskReg.payments?.some((p: any) => p.method === 'stripe') ? (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      Stripe payments detected. Refund will be processed automatically via Stripe.
                    </Alert>
                  ) : (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      No Stripe payments found. Refund must be processed manually.
                    </Alert>
                  )}
                </>
              ) : (
                <CircularProgress />
              )}
              
              <Divider sx={{ my: 2 }} />
              
              {/* Admin Comment */}
              <Typography variant="h6" gutterBottom>Admin Decision</Typography>
              {selectedTask.type === 'registration_cancellation_review' && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  Approve/Decline in this dialog will resolve this cancellation review task.
                  Complete any other checks first.
                </Alert>
              )}
              <TextField
                label="Comment (optional)"
                multiline
                rows={3}
                fullWidth
                value={adminComment}
                onChange={(e) => setAdminComment(e.target.value)}
                placeholder="Enter any notes about this refund decision..."
                sx={{ mb: 2 }}
              />
              
              {/* Decline email option */}
              <FormControlLabel
                control={
                  <Checkbox
                    checked={sendApproveEmail}
                    onChange={(e) => setSendApproveEmail(e.target.checked)}
                  />
                }
                label="Send email notification if approved (refund_validated)"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={sendDeclineEmail}
                    onChange={(e) => setSendDeclineEmail(e.target.checked)}
                  />
                }
                label="Send email notification if declined"
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRefundDialogOpen(false)} disabled={processing}>
            Cancel
          </Button>
          <Button
            onClick={handleDeclineRefund}
            color="error"
            variant="outlined"
            disabled={processing}
          >
            {processing ? <CircularProgress size={20} /> : 'Decline Refund'}
          </Button>
          <Button
            onClick={handleApproveRefund}
            color="success"
            variant="contained"
            disabled={processing || !selectedTaskReg || Number(refundAmount) <= 0}
          >
            {processing ? <CircularProgress size={20} /> : 'Approve Refund'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Auto-Link Review Dialog */}
      <Dialog open={autoLinkDialogOpen} onClose={() => setAutoLinkDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Review Profile Link Request</DialogTitle>
        <DialogContent>
          {autoLinkError && <Alert severity="error" sx={{ mb: 2 }}>{autoLinkError}</Alert>}
          
          {selectedAutoLinkTask && (
            <Box>
              <Alert severity="info" sx={{ mb: 2 }}>
                A new user signed up with an email that matches an existing profile. 
                Review the details below and decide whether to link them.
              </Alert>

              {/* New Auth User Info */}
              <Typography variant="h6" gutterBottom>New Authenticated User</Typography>
              <Box sx={{ bgcolor: 'action.hover', p: 2, borderRadius: 1, mb: 3 }}>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                  <Typography><strong>Email:</strong> {selectedAutoLinkTask.authEmail || 'N/A'}</Typography>
                  <Typography><strong>Display Name:</strong> {selectedAutoLinkTask.authDisplayName || 'N/A'}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Auth UID: {selectedAutoLinkTask.authUid}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    New Doc ID: {selectedAutoLinkTask.newUserDocId}
                  </Typography>
                </Box>
              </Box>

              <Divider sx={{ my: 2 }}>
                <Chip label={`Match: ${selectedAutoLinkTask.matchType || 'email'}`} size="small" />
              </Divider>

              {/* Existing Profile Info */}
              <Typography variant="h6" gutterBottom>Existing Profile (to link to)</Typography>
              <Box sx={{ bgcolor: 'action.hover', p: 2, borderRadius: 1, mb: 3 }}>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                  <Typography>
                    <strong>Name:</strong> {selectedAutoLinkTask.existingDisplayName || 
                      `${selectedAutoLinkTask.existingFirstName || ''} ${selectedAutoLinkTask.existingLastName || ''}`.trim() || 'N/A'}
                  </Typography>
                  <Typography><strong>Email:</strong> {selectedAutoLinkTask.existingEmail || 'N/A'}</Typography>
                  {selectedAutoLinkTask.existingPersonId && (
                    <Typography><strong>Person ID:</strong> {selectedAutoLinkTask.existingPersonId}</Typography>
                  )}
                  {selectedAutoLinkTask.existingYearOfBirth && (
                    <Typography><strong>Year of Birth:</strong> {selectedAutoLinkTask.existingYearOfBirth}</Typography>
                  )}
                  {selectedAutoLinkTask.existingNationality && (
                    <Typography><strong>Nationality:</strong> {selectedAutoLinkTask.existingNationality}</Typography>
                  )}
                  <Typography variant="caption" color="text.secondary">
                    Doc ID: {selectedAutoLinkTask.existingDocId}
                  </Typography>
                </Box>
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* What happens on approval */}
              <Alert severity="warning" sx={{ mb: 2 }}>
                <strong>If approved:</strong> The new user's temporary profile will be deleted, and they will be linked to the existing profile. 
                Any race results or registrations will be associated with the existing profile.
              </Alert>

              <Alert severity="info" sx={{ mb: 2 }}>
                <strong>If rejected:</strong> The new user keeps their separate profile. 
                They can claim the existing profile later via "This is me" if needed.
              </Alert>

              {/* Admin Comment */}
              <TextField
                label="Admin Comment (optional)"
                multiline
                rows={2}
                fullWidth
                value={autoLinkComment}
                onChange={(e) => setAutoLinkComment(e.target.value)}
                placeholder="Add any notes about this decision..."
                sx={{ mt: 2 }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAutoLinkDialogOpen(false)} disabled={autoLinkProcessing}>
            Cancel
          </Button>
          <Button
            onClick={handleRejectAutoLink}
            color="error"
            variant="outlined"
            disabled={autoLinkProcessing}
          >
            {autoLinkProcessing ? <CircularProgress size={20} /> : 'Reject (Keep Separate)'}
          </Button>
          <Button
            onClick={handleApproveAutoLink}
            color="success"
            variant="contained"
            disabled={autoLinkProcessing}
          >
            {autoLinkProcessing ? <CircularProgress size={20} /> : 'Approve Link'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Claim/Unclaim Review Dialog */}
      <Dialog open={claimDialogOpen} onClose={() => setClaimDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {selectedClaimTask?.type === 'claim_profile' ? 'Review Profile Claim Request' : 'Review Profile Unlink Request'}
        </DialogTitle>
        <DialogContent>
          {claimError && <Alert severity="error" sx={{ mb: 2 }}>{claimError}</Alert>}
          
          {selectedClaimTask && (
            <Box>
              {selectedClaimTask.type === 'claim_profile' ? (
                <>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    A user claims that a runner profile belongs to them. 
                    Review the details below and decide whether to link them.
                  </Alert>

                  {/* Requester Info */}
                  <Typography variant="h6" gutterBottom>Requester (wants to claim)</Typography>
                  <Box sx={{ bgcolor: 'action.hover', p: 2, borderRadius: 1, mb: 3 }}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                      <Typography><strong>Name:</strong> {selectedClaimTask.requesterName || 'N/A'}</Typography>
                      <Typography><strong>Email:</strong> {selectedClaimTask.requesterEmail || 'N/A'}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Auth UID: {selectedClaimTask.requesterUid}
                      </Typography>
                    </Box>
                  </Box>

                  <Divider sx={{ my: 2 }}>
                    <Chip label="Claims to be" size="small" />
                  </Divider>

                  {/* Target Profile Info */}
                  <Typography variant="h6" gutterBottom>Target Profile (to be claimed)</Typography>
                  <Box sx={{ bgcolor: 'action.hover', p: 2, borderRadius: 1, mb: 3 }}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                      <Typography><strong>Name:</strong> {selectedClaimTask.targetName || 'N/A'}</Typography>
                      <Typography><strong>Email:</strong> {selectedClaimTask.targetEmail || 'N/A'}</Typography>
                      {selectedClaimTask.targetPersonId && (
                        <Typography><strong>Person ID:</strong> {selectedClaimTask.targetPersonId}</Typography>
                      )}
                      {selectedClaimTask.targetYearOfBirth && (
                        <Typography><strong>Year of Birth:</strong> {selectedClaimTask.targetYearOfBirth}</Typography>
                      )}
                      {selectedClaimTask.targetNationality && (
                        <Typography><strong>Nationality:</strong> {selectedClaimTask.targetNationality}</Typography>
                      )}
                      <Typography variant="caption" color="text.secondary">
                        Doc ID: {selectedClaimTask.targetProfileDocId}
                      </Typography>
                    </Box>
                  </Box>

                  <Alert severity="warning" sx={{ mb: 2 }}>
                    <strong>Legacy approval:</strong> The requester account would be linked to the target profile and a separate requester profile may be marked as merged. Historical registrations and race results are not automatically moved.
                  </Alert>
                  {isStrictIdentityPolicy() && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      This claim request is advisory. Strict identity mode requires a controlled identity review before account ownership changes; approval is unavailable here.
                    </Alert>
                  )}
                </>
              ) : (
                <>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    A user wants to unlink their account from a runner profile. 
                    This typically means they were incorrectly linked to someone else's data.
                  </Alert>

                  {/* Requester Info */}
                  <Typography variant="h6" gutterBottom>Requester (wants to unlink)</Typography>
                  <Box sx={{ bgcolor: 'action.hover', p: 2, borderRadius: 1, mb: 3 }}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                      <Typography><strong>Name:</strong> {selectedClaimTask.requesterName || 'N/A'}</Typography>
                      <Typography><strong>Email:</strong> {selectedClaimTask.requesterEmail || 'N/A'}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Auth UID: {selectedClaimTask.requesterUid}
                      </Typography>
                    </Box>
                  </Box>

                  <Divider sx={{ my: 2 }}>
                    <Chip label="Wants to unlink from" size="small" />
                  </Divider>

                  {/* Profile Info - support both old (profileDocId) and new (targetProfileDocId) field names */}
                  <Typography variant="h6" gutterBottom>Profile (to be unlinked)</Typography>
                  <Box sx={{ bgcolor: 'action.hover', p: 2, borderRadius: 1, mb: 3 }}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                      <Typography><strong>Name:</strong> {selectedClaimTask.targetName || selectedClaimTask.profileName || 'N/A'}</Typography>
                      <Typography><strong>Email:</strong> {selectedClaimTask.targetEmail || selectedClaimTask.profileEmail || 'N/A'}</Typography>
                      {(selectedClaimTask.targetPersonId || selectedClaimTask.personId) && (
                        <Typography><strong>Person ID:</strong> {selectedClaimTask.targetPersonId || selectedClaimTask.personId}</Typography>
                      )}
                      <Typography variant="caption" color="text.secondary">
                        Profile Doc ID: {selectedClaimTask.targetProfileDocId || selectedClaimTask.profileDocId}
                      </Typography>
                      {selectedClaimTask.legacySourceUserId && (
                        <Typography variant="caption" color="text.secondary">
                          Legacy Source Doc: {selectedClaimTask.legacySourceUserId}
                        </Typography>
                      )}
                    </Box>
                  </Box>

                  {selectedClaimTask.reason && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      <strong>Reason provided:</strong> {selectedClaimTask.reason}
                    </Alert>
                  )}

                  <Alert severity="warning" sx={{ mb: 2 }}>
                    <strong>If approved:</strong> The user will be unlinked from this profile's race history.
                    {selectedClaimTask.keepPersonalDetails !== false 
                      ? ' Personal details (name, email, etc.) will be kept.'
                      : ' A new empty user document will be created for them.'}
                    {selectedClaimTask.legacySourceUserId && 
                      ` The legacy profile (${selectedClaimTask.legacySourceUserId}) will be archived.`}
                  </Alert>
                </>
              )}

              {/* Admin Comment */}
              <TextField
                label="Admin Notes (optional)"
                multiline
                rows={2}
                fullWidth
                value={claimComment}
                onChange={(e) => setClaimComment(e.target.value)}
                placeholder="Add any notes about this decision..."
                sx={{ mt: 2 }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setClaimDialogOpen(false)} disabled={claimProcessing}>
            Cancel
          </Button>
          <Button
            onClick={handleRejectClaim}
            color="error"
            variant="outlined"
            disabled={claimProcessing}
          >
            {claimProcessing ? <CircularProgress size={20} /> : 'Reject'}
          </Button>
          <Button
            onClick={handleApproveClaim}
            color="success"
            variant="contained"
            disabled={claimProcessing || (selectedClaimTask?.type === 'claim_profile' && isStrictIdentityPolicy())}
          >
            {claimProcessing ? <CircularProgress size={20} /> : 'Approve'}
          </Button>
        </DialogActions>
      </Dialog>

      {accountBindingReviewTask && accountBindingReviewController && (
        <AccountBindingReviewDialog
          key={accountBindingReviewTask.id}
          open
          controller={accountBindingReviewController}
          kind={accountReviewKind}
          onClose={closeAccountBindingReview}
          onApplied={closeAccountBindingReview}
        />
      )}
      {unclaimPrepareTask && (
        <ReviewedUnclaimPrepareDialog
          open taskId={unclaimPrepareTask.id} transport={unclaimPrepareTransport}
          onClose={() => setUnclaimPrepareTask(null)}
          onPrepared={controller => {
            if (!unclaimPrepareActor || auth.currentUser?.uid !== unclaimPrepareActor) {
              setUnclaimPrepareTask(null);
              setAccountBindingReviewError('The signed-in account changed. Open the task again.');
              return;
            }
            setAccountReviewKind('unclaim');
            setAccountBindingReviewTask(unclaimPrepareTask);
            setAccountBindingReviewController(controller);
            setUnclaimPrepareTask(null);
          }}
        />
      )}
      {bindingPrepareActor && (
        <AccountBindingPrepareDialog
          open transport={bindingPrepareTransport} onClose={() => setBindingPrepareActor(null)}
          onPrepared={controller => {
            const taskId = controller.getState().taskId;
            if (!taskId || auth.currentUser?.uid !== bindingPrepareActor) {
              setBindingPrepareActor(null);
              setAccountBindingReviewError('The signed-in account changed. Open the review again.');
              return;
            }
            setAccountReviewKind('binding');
            setAccountBindingReviewTask({ registrationId: '', id: taskId, type: 'review_account_binding', status: 'open' });
            setAccountBindingReviewController(controller);
            setBindingPrepareActor(null);
          }}
        />
      )}
    </>
  );
};

export default AdminTasksPanel;
